import React, { useState, useEffect, useRef, Fragment } from 'react';
import styled from 'styled-components';
import { Draggable } from 'react-beautiful-dnd';
import {
  Switch,
  TextField,
  IconButton,
  Accordion,
  FormControlLabel,
  AccordionDetails,
  AccordionSummary
} from '@material-ui/core';
import {
  ExpandMore as ExpandMoreIcon,
  RemoveCircleOutline as RemoveCircleOutlineIcon
} from '@material-ui/icons';
import { Shape, Workflow, StateNode, StateEvent } from '@sierralabs/workflow';

import { useTimeout } from '../../../common/timeout.hook';
import ConfirmDialog from '../../../common/dialogs/ConfirmDialog';
import { WorkflowTaskTypeEntity } from '../Workflow.interface';
import StateEventConditions from './StateEventConditions';
import StateTransitionSelector from './StateTransitionSelector';

export const Styled = {
  Accordion: styled(Accordion)`
    .MuiAccordionSummary-root {
      padding-left: 5px;
      &.Mui-expanded {
        min-height: unset;
      }
      .MuiAccordionSummary-content {
        margin: 12px 0px;
        align-items: center;
        .delete-button {
          margin-right: 7px;
        }
      }
    }
  `
};

export interface StateEventEditorProps {
  shape: Shape;
  event: StateEvent;
  workflow: Workflow;
  type?: WorkflowTaskTypeEntity;
  index: number;
  expanded?: boolean;
  updateShape: () => void;
  updateWorkflow: (shouldRefresh?: boolean) => void;
  setExpanded?: (expanded: boolean) => void;
  setIsDragging?: (isDragging: boolean) => void;
}

const StateEventEditor: React.FC<StateEventEditorProps> = (
  props: StateEventEditorProps
) => {
  const {
    shape,
    event,
    type,
    workflow,
    index,
    expanded = false,
    setExpanded,
    setIsDragging,
    updateShape,
    updateWorkflow
  } = props;
  const eventShapeRef = useRef<Shape | undefined>();
  const [debounceDrag] = useTimeout();
  const [isDragging, setIsDraggingState] = useState<boolean>(false);
  const [isOpenDeleteDialog, setIsOpenDeleteDialg] = useState(false);

  const getEventShape = (): Shape | undefined => {
    if (eventShapeRef.current) {
      return eventShapeRef.current;
    }
    // get Event type shape with same event data
    eventShapeRef.current = shape.parent?.shapes.find(
      (shape: Shape) => shape.data === event
    );
    return eventShapeRef.current;
  };

  const setEventShapeName = (value: string) => {
    const eventShape: Shape | undefined = getEventShape();
    if (eventShape) {
      eventShape.name = event.description || (value ? value + '?' : '');
    }
  };

  useEffect(() => {
    setIsDragging?.(isDragging);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging]);

  if (!event.transitions || event.transitions.length === 0) {
    // make sure there is always at least one target in the transition defined
    // otherwise field won't show up
    event.transitions = [{ target: event.stateNode.path }];
  }
  return (
    <Draggable {...{ index }} draggableId={`item-${index}`}>
      {(provided, snapshot) => {
        debounceDrag(() => {
          isDragging !== snapshot.isDragging &&
            setIsDraggingState(snapshot.isDragging);
        });
        return (
          <Styled.Accordion
            ref={provided.innerRef}
            {...{ expanded }}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            onChange={(_, expanded: boolean) => setExpanded?.(expanded)}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              className='transition-panel'
            >
              <IconButton
                size='small'
                className='delete-button'
                onClick={event => {
                  event.stopPropagation();
                  setIsOpenDeleteDialg(true);
                }}
              >
                <RemoveCircleOutlineIcon />
              </IconButton>
              {event.name || 'New Transition'}
            </AccordionSummary>
            <AccordionDetails>
              <div className='form-fields'>
                <TextField
                  fullWidth
                  size='small'
                  variant='outlined'
                  label='Event Name'
                  className='field'
                  value={event.name || ''}
                  onChange={ev => {
                    const { value = '' } = ev.target;
                    event.name = value;
                    setEventShapeName(value);
                    updateShape();
                  }}
                  onBlur={() => updateWorkflow()}
                />
                <TextField
                  fullWidth
                  size='small'
                  variant='outlined'
                  className='field'
                  label='Event Description'
                  value={event.description || ''}
                  onChange={e => {
                    const { value = '' } = e.target;
                    event.description = value;
                    setEventShapeName(event.name || value);
                    updateShape();
                  }}
                  onBlur={() => updateWorkflow(false)}
                />
                <FormControlLabel
                  className='field'
                  label='Is Transient?'
                  control={
                    <Switch
                      color='primary'
                      checked={event.isTransient || false}
                      onChange={(_, checked: boolean) => {
                        event.isTransient = checked;
                        updateShape();
                        updateWorkflow();
                      }}
                    />
                  }
                />
                {event.transitions.map((transition, index) => (
                  <Fragment key={index}>
                    <StateTransitionSelector
                      {...{ shape, workflow, transition }}
                      onChange={(
                        stateNode: StateNode | undefined,
                        prevTarget: string
                      ) => {
                        if (stateNode?.path) {
                          transition.target = stateNode.path;
                          event.updateTransitions(event.transitions);
                          updateWorkflow(false);
                        }
                      }}
                    />
                    <StateEventConditions
                      {...{ event, type, transition }}
                      onChange={transitionInner => {
                        event.transitions[index] = transitionInner;
                        updateWorkflow(false);
                      }}
                    />
                  </Fragment>
                ))}
              </div>
            </AccordionDetails>
            {isOpenDeleteDialog && (
              <ConfirmDialog
                title={`Delete ${event.name} transition`}
                message='Are you sure you want to delete this transition?'
                open={isOpenDeleteDialog}
                onCancel={() => setIsOpenDeleteDialg(false)}
                onConfirm={() => {
                  setIsOpenDeleteDialg(false);
                  event.remove();
                  updateWorkflow();
                }}
              />
            )}
          </Styled.Accordion>
        );
      }}
    </Draggable>
  );
};

export default StateEventEditor;
